import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useMount from 'common/hooks/useMount';
import { Skeleton, Row, Col } from 'antd';
import { ProfileOutlined } from '@ant-design/icons';

import { Layout, Header, Steps } from 'common/components';

import InformationsFeature from './features/informations';
import InvestingFeature from './features/investing';
import ProtocolFeature from './features/protocol';
import AgreementFeature from './features/agreement';
import TransactionFeature from './features/transaction';
import BestOfTempletonFeature from './features/BoT';
import VerificationFeature from './features/verification';
import {
  loadTransactionActions,
  selectFormTransaction,
  selectFormInformationsDone,
  selectInvestingDone,
  selectProtocolDone,
  selectAgreementDone,
  selectBestOfTempletonDone,
  selectStrategy,
  InvestingStrategy,
  selectIsFormVerified,
  selectInformationsCustomerInfo1,
  selectInformationsCustomer1FullName,
} from './ducks';

export interface FeatureProps {
  isPrivileged?: boolean;
}

function FormFeature() {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const transaction = useSelector(selectFormTransaction);
  const { isVerifiedEmail, isVerifiedPhone } = useSelector(selectIsFormVerified);

  const verificationDone = isVerifiedEmail && isVerifiedPhone;
  const informationsDone = useSelector(selectFormInformationsDone);
  const investingDone = useSelector(selectInvestingDone);
  const protocolDone = useSelector(selectProtocolDone);
  const agreementDone = useSelector(selectAgreementDone);
  const bestOfTempletonDone = useSelector(selectBestOfTempletonDone);
  const strategy = useSelector(selectStrategy);
  const customerInfo = useSelector(selectInformationsCustomerInfo1);
  const customerFullName = useSelector(selectInformationsCustomer1FullName);

  useMount(() => {
    dispatch(loadTransactionActions.request(id!));
  });

  function handleStepClick(step: string) {
    return () => {
      if (step === 'verification') {
        navigate(`/form/${id}/verification`);
      } else if (step === 'informations' && informationsDone) {
        navigate(`/form/${id}/informations`);
      } else if (step === 'investing' && investingDone) {
        navigate(`/form/${id}/investing`);
      } else if (step === 'protocol' && protocolDone) {
        navigate(`/form/${id}/protocol`);
      } else if (step === 'agreement' && agreementDone) {
        navigate(`/form/${id}/agreement`);
      } else if (
        step === 'BoT' &&
        bestOfTempletonDone &&
        strategy &&
        strategy.type !== InvestingStrategy.Conservative
      ) {
        navigate(`/form/${id}/BoT`);
      }
    };
  }

  const stepDefs: Array<{ className?: string; content: string; key: string; status: 'finish' | 'process' }> = [
    {
      key: 'verification',
      content: t('tabs.verification'),
      status: verificationDone ? 'finish' : 'process',
      className: 'steps-item-skippable',
    },
    { key: 'informations', content: t('tabs.informations'), status: informationsDone ? 'finish' : 'process' },
    { key: 'investing', content: t('tabs.investing'), status: investingDone ? 'finish' : 'process' },
    { key: 'protocol', content: t('tabs.protocol'), status: protocolDone ? 'finish' : 'process' },
    { key: 'agreement', content: t('tabs.agreement'), status: agreementDone ? 'finish' : 'process' },
    ...(strategy && strategy.type !== InvestingStrategy.Conservative
      ? [
          {
            key: 'BoT',
            content: t('tabs.bestOfTempleton'),
            status: (bestOfTempletonDone ? 'finish' : 'process') as 'finish' | 'process',
          },
        ]
      : []),
  ];

  return (
    <Layout>
      <Header type="tall">
        <Row align="top">
          <Col span={21}>
            <Steps
              size="small"
              onChange={(index) => {
                handleStepClick(stepDefs[index].key)();
              }}
              items={stepDefs.map(({ content, status, className }) => ({ content, status, className }))}
            />
          </Col>
          {customerFullName ? (
            <Col className="form-progress-client-info" span={0} md={3}>
              <p>
                <ProfileOutlined /> {customerFullName}
              </p>

              {customerInfo?.birthday ? <p>{customerInfo.birthday.date}</p> : null}
            </Col>
          ) : null}
        </Row>
      </Header>
      <Layout.Content>
        {transaction ? (
          <Layout.Container>
            <Routes>
              <Route path="verification" element={<VerificationFeature />} />
              <Route path="informations/*" element={<InformationsFeature />} />
              <Route path="investing/*" element={<InvestingFeature />} />
              <Route path="protocol/*" element={<ProtocolFeature />} />
              <Route path="agreement/*" element={<AgreementFeature />} />
              <Route path="transaction" element={<TransactionFeature />} />
              <Route path="BoT" element={<BestOfTempletonFeature />} />
              <Route path="*" element={<Navigate to="../verification" replace />} />
            </Routes>
          </Layout.Container>
        ) : (
          <Skeleton />
        )}
      </Layout.Content>
    </Layout>
  );
}

export default FormFeature;
